import { useCallback, useState } from 'react';
import { type z } from 'zod';

import { isDefined } from '../utils/types.js';

export enum BrowserStorageVariant {
  /**
   * Use LocalStorage for persistent storage that does not expire.
   * It will be available even after the browser is closed and reopened.
   * Never store private information, secrets, PHI, PII, or the like in LocalStorage.
   */
  LocalStorage = 'LocalStorage',
  /**
   * Use SessionStorage for storage that is available only for the duration of the page session.
   * It will be cleared when the page session ends.
   * For example, when you open a new tab you will have a new session.
   * If you duplicate a tab, you will clone the session, which will then have its own lifecycle.
   */
  SessionStorage = 'SessionStorage',
}

const BROWSER_STORAGE: Record<BrowserStorageVariant, Storage> = {
  [BrowserStorageVariant.LocalStorage]: window.localStorage,
  [BrowserStorageVariant.SessionStorage]: window.sessionStorage,
};

export interface UseBrowserStorageProps<T> {
  storageKeyName: string;

  /**
   * variant: BrowserStorageVariant.LocalStorage | BrowserStorageVariant.SessionStorage
   */
  variant: BrowserStorageVariant;
  schema: z.ZodSchema<T>;

  /**
   * Initial value to use when browser storage values are not present, or are invalid.
   */
  initialValue: T;
}

/**
 * Provide a type-safe way to interact with browserStorage (e.g. localStorage, sessionStorage).
 * - LocalStorage
 *    - It will be available even after the browser is closed and reopened.
 *    - Used for persistent storage that does not expire.
 *    - Do not store private information, secrets, PHI, PII, or the like in LocalStorage.
 * - SessionStorage
 *   - It will be cleared when the page session ends.
 *   - Used for storage that is available only for the duration of the page session.
 *   - For example, when you open a new tab you will have a new session.
 *   - If you duplicate a tab, you will clone the session, which will then have its own lifecycle.
 *
 * The goal is to parse and validate browser storage to match your schema, without type casting.
 * This allows you to trust that the browser storage parameters are shaped as expected, and falls back to an empty object if not.
 */
export function useBrowserStorage<T>(props: UseBrowserStorageProps<T>) {
  const { storageKeyName, schema, initialValue, variant } = props;
  const storage = BROWSER_STORAGE[variant];

  const [storedValues, _setStoredValuesState] = useState<T>(() => {
    try {
      const storageValue = storage.getItem(storageKeyName);
      if (!isDefined(storageValue)) {
        return initialValue;
      }
      const parsedValue = JSON.parse(storageValue);
      return schema.parse(parsedValue);
    } catch {
      return initialValue;
    }
  });

  const setStoredValues = useCallback(
    (newValues: T) => {
      BROWSER_STORAGE[variant].setItem(storageKeyName, JSON.stringify(newValues));
      _setStoredValuesState(newValues);
    },
    [storageKeyName, variant]
  );

  const updateStoredValue = useCallback(
    <Field extends keyof T>(fieldName: Field, newValue: T[Field]) => {
      const newValues = { ...storedValues, [fieldName]: newValue } as T;
      return setStoredValues(newValues);
    },
    [setStoredValues, storedValues]
  );

  return {
    storedValues,
    setStoredValues,
    updateStoredValue,
  };
}
